/* iter55z-l — MyBillingPanel: customer-facing subscription + card
 * management surface. Zero card data ever touches our server — the
 * "Update card" and "Cancel plan" flows redirect to Stripe's hosted
 * Customer Portal.
 *
 * States:
 *   • No customer yet          → CTA "Choose a plan" (points to PricingDialog)
 *   • Has customer + active    → Card block + "Manage subscription" button
 *   • Has customer + past_due  → Amber warning + "Update card" CTA
 *   • Has customer + cancelled → Grey block + "Resubscribe" CTA
 *
 * The panel autoloads on mount and polls every 30s so the moment the
 * user returns from Stripe's portal (card updated / plan swapped) the
 * new state appears without a manual refresh.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/lib/auth_context';
import {
  CreditCard, ExternalLink, Loader2, RefreshCw, AlertTriangle,
  CheckCircle2, XCircle, Sparkles, FileCheck2, Download, TrendingUp,
  ArrowRight,
} from 'lucide-react';
import HiatusCard from './HiatusCard';       // iter55g30
import RoyaltiesCard from './RoyaltiesCard';
import OverageToggleCard from './OverageToggleCard'; // iter55g31
import AiShareTrendSparkline from './AiShareTrendSparkline';   // iter-p3-v
import BonusAddonsShelf from '@/components/BonusAddonsShelf';   // Feb-2026 revenue lifts
import ReferralCard from '@/components/ReferralCard';           // Feb-2026 revenue lifts
import MilestonesCard from '@/components/MilestonesCard';       // Feb-2026 revenue lifts
import GrandfatherBadge from '@/components/GrandfatherBadge';   // Feb-2026 grandfather pricing
import CancelSubscriptionDialog from '@/components/dialogs/CancelSubscriptionDialog'; // Feb-2026 winback
import ZernioAnalyticsCard from './ZernioAnalyticsCard';        // Feb-2026 Zernio marketing ROI

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const POLL_MS = 30_000;

function brandLabel(brand) {
  if (!brand) return 'Card';
  const norm = String(brand).toLowerCase();
  const map = {
    visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express',
    discover: 'Discover', diners: 'Diners Club', jcb: 'JCB', unionpay: 'UnionPay',
    unknown: 'Card',
  };
  return map[norm] || (brand.charAt(0).toUpperCase() + brand.slice(1));
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso; }
}

function StatusBadge({ status }) {
  if (!status) return null;
  const tone =
    status === 'active'    ? 'bg-emerald-100 text-emerald-800 border-emerald-300/70' :
    status === 'trialing'  ? 'bg-sky-100 text-sky-800 border-sky-300/70' :
    status === 'past_due'  ? 'bg-amber-100 text-amber-900 border-amber-300/70' :
    status === 'cancelled' ? 'bg-muted text-muted-foreground border-muted-foreground/30' :
                              'bg-secondary text-foreground border-border';
  return (
    <span
      data-testid="my-billing-status-badge"
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wider ${tone}`}
    >
      {status === 'active'   && <CheckCircle2 className="h-3 w-3" />}
      {status === 'past_due' && <AlertTriangle className="h-3 w-3" />}
      {status === 'cancelled' && <XCircle className="h-3 w-3" />}
      {status.replace('_', ' ')}
    </span>
  );
}

/** iter-p2-v — Recommended-tier callout for the MyBillingPanel usage
 *  section.  Hits `/api/subscriptions/tier-recommendation` and renders
 *  a green (upgrade) or amber (downgrade) mini-banner with a one-tap
 *  Choose-Plan button.  Renders nothing when the recommendation is
 *  `stay` so the panel stays quiet for balanced users. */
function TierRecommendationCallout({ onChoosePlan }) {
  const [rec, setRec] = useState(null);
  useEffect(() => {
    axios.get(`${API}/subscriptions/tier-recommendation`, { withCredentials: true })
      .then((r) => setRec(r.data)).catch(() => setRec(null));
  }, []);
  if (!rec) return null;
  if (rec.recommendation === 'upgrade') {
    return (
      <div className="mt-3 flex items-start gap-2 rounded-md border border-primary/50 bg-primary/5 p-2.5"
           data-testid="my-billing-tier-recommendation-upgrade">
        <Sparkles className="mt-0.5 h-3.5 w-3.5 flex-none text-primary" />
        <div className="min-w-0 flex-1">
          <div className="text-[11.5px] font-semibold">
            Try <span className="text-primary">{rec.target_tier_name}</span> — you&rsquo;re at {rec.recent_pct}% of your allowance
          </div>
          <div className="mt-0.5 text-[10.5px] text-muted-foreground">
            +${rec.delta_usd}/mo · +{(rec.delta_tokens || 0).toLocaleString()} tokens · avoids running out mid-manuscript.
          </div>
        </div>
        <Button size="sm" className="h-7 px-2 text-[10.5px]"
                onClick={onChoosePlan}
                data-testid="my-billing-tier-recommendation-btn">
          Upgrade
        </Button>
      </div>
    );
  }
  if (rec.recommendation === 'downgrade') {
    return (
      <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 dark:border-amber-800/60 dark:bg-amber-900/20"
           data-testid="my-billing-tier-recommendation-downgrade">
        <ArrowRight className="mt-0.5 h-3.5 w-3.5 flex-none text-amber-700" />
        <div className="min-w-0 flex-1">
          <div className="text-[11.5px] font-semibold">
            Consider <span className="text-amber-800">{rec.target_tier_name}</span> — you&rsquo;ve only used {rec.recent_pct}% lately
          </div>
          <div className="mt-0.5 text-[10.5px] text-muted-foreground">
            Save ${rec.savings_usd}/mo. You can upgrade back the moment you need more capacity.
          </div>
        </div>
        <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px]"
                onClick={onChoosePlan}
                data-testid="my-billing-tier-recommendation-downgrade-btn">
          Switch
        </Button>
      </div>
    );
  }
  return null;
}

export default function MyBillingPanel({ onChoosePlan }) {
  const [cancelOpen, setCancelOpen] = React.useState(false);   // Feb-2026 winback dialog
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [openingPortal, setOpeningPortal] = useState(false);
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/subscriptions/me`, { withCredentials: true });
      setData(r.data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, POLL_MS);
    return () => clearInterval(iv);
  }, [load]);

  // iter55z-q — First-time contextual tour for My Billing (only when we
  // actually have something to show — i.e. the user has a Stripe customer).
  useEffect(() => {
    if (!data?.has_customer) return undefined;
    const t = setTimeout(() => {
      import('@/lib/tours').then(({ runTour }) => runTour('my-billing-open'));
    }, 700);
    return () => clearTimeout(t);
  }, [data?.has_customer]);

  const openPortal = async () => {
    if (openingPortal) return;
    setOpeningPortal(true);
    try {
      const r = await axios.post(`${API}/subscriptions/portal`,
        { return_url: window.location.href },
        { withCredentials: true });
      if (r.data?.url) {
        window.location.assign(r.data.url);
      } else {
        throw new Error('No portal URL');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open the billing portal.');
      setOpeningPortal(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-md border border-border bg-card p-6 text-center text-[12px] text-muted-foreground" data-testid="my-billing-loading">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" />
        <div className="mt-1">Loading your subscription…</div>
      </div>
    );
  }

  if (!data) return null;

  /* iter55f60 — ULA receipt card. Renders whenever the account has a
   * recorded acceptance timestamp (`trial_ula_accepted_at`). Users get
   * a permanent copy of what they clicked to accept, keyed to the
   * exact ULA version they saw. Download hits the backend receipt
   * endpoint which streams a reportlab-generated PDF. */
  const ulaAcceptedAt = user?.trial_ula_accepted_at;
  const ulaCard = ulaAcceptedAt && (
    <div
      className="rounded-md border border-border bg-card p-4"
      data-testid="my-billing-ula-receipt"
    >
      <div className="flex items-start gap-3">
        <FileCheck2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="flex-1">
          <div className="text-[12.5px] font-semibold">14-day Trial Agreement</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            You accepted the ULA on <b>{fmtDate(ulaAcceptedAt)}</b>
            {user?.trial_ula_version ? <> &middot; version <span className="uppercase tracking-wider">{user.trial_ula_version}</span></> : null}.
          </div>
        </div>
        <Button
          asChild
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-[11.5px]"
          data-testid="my-billing-ula-download"
        >
          <a
            href={`${API}/auth/ula/receipt.pdf`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Download className="h-3.5 w-3.5" />
            Download PDF copy
          </a>
        </Button>
      </div>
    </div>
  );

  const {
    tier, cadence, status, current_period_end, has_customer, plan, card,
  } = data;

  const priceObj = plan ? (cadence === 'yearly' ? plan.yearly : plan.monthly) : null;
  const price    = priceObj?.price_usd;

  // ---------- No customer yet — offer to subscribe ----------
  if (!has_customer) {
    return (
      <div className="space-y-3">
        <div className="rounded-md border border-border bg-card p-4" data-testid="my-billing-no-subscription">
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-primary/15 text-primary">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="text-[12.5px] font-semibold">No active subscription</div>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              You&apos;re on the free tier. Upgrade to unlock full-length novel generation,
              Copyscape scans, and the bookstore.
            </p>
          </div>
          <Button
            size="sm"
            onClick={onChoosePlan}
            data-testid="my-billing-choose-plan"
            className="h-8 gap-1.5 text-[11.5px]"
          >
            <Sparkles className="h-3.5 w-3.5" /> Choose a plan
          </Button>
        </div>
        </div>
        {ulaCard}
        {/* Add-ons are available on all tiers */}
        <BonusAddonsShelf />
      </div>
    );
  }

  // ---------- Has customer — render subscription + card ----------
  return (
    <div className="space-y-3">
    <div className="rounded-md border border-border bg-card" data-testid="my-billing-panel">
      <div className="flex items-start justify-between gap-3 border-b border-border p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-primary/15 text-primary">
            <CreditCard className="h-4 w-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[12.5px] font-semibold" data-testid="my-billing-tier">
                {plan?.name || tier || 'Subscription'}
              </span>
              <StatusBadge status={status} />
            </div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              {price !== undefined && cadence
                ? <>
                    ${Number(price).toFixed(2)}
                    <span className="opacity-70">/{cadence === 'yearly' ? 'year' : 'month'}</span>
                  </>
                : <>Managed via Stripe</>}
              {' · '}
              Next renewal: <b className="text-foreground" data-testid="my-billing-renewal">{fmtDate(current_period_end)}</b>
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          className="h-7 gap-1 text-[11px]"
          data-testid="my-billing-refresh"
          title="Refresh from Stripe"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* iter-p2-t — Usage bar: tokens + LLM $ this billing period */}
      {data.usage && (data.usage.tokens_included > 0 || data.usage.llm_cap_usd > 0) && (
        <div className="border-b border-border p-4" data-testid="my-billing-usage">
          <div className="mb-2 text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">This month</div>
          {data.usage.tokens_included > 0 && (
            <div className="mb-3" data-testid="my-billing-tokens-bar">
              <div className="mb-1 flex items-baseline justify-between text-[11.5px]">
                <span>AI tokens used</span>
                <span className="font-mono-stat text-muted-foreground">
                  {data.usage.tokens_used.toLocaleString()} / {data.usage.tokens_included.toLocaleString()}
                  <span className="ml-1 opacity-70">({data.usage.tokens_pct.toFixed(1)}%)</span>
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-sm bg-secondary">
                <div
                  className={
                    'h-full transition-all ' +
                    (data.usage.tokens_pct >= 90 ? 'bg-rose-600'
                      : data.usage.tokens_pct >= 70 ? 'bg-amber-500' : 'bg-primary')
                  }
                  style={{ width: `${Math.min(100, data.usage.tokens_pct)}%` }}
                />
              </div>
            </div>
          )}
          {data.usage.llm_cap_usd > 0 && (
            <div data-testid="my-billing-llm-bar">
              <div className="mb-1 flex items-baseline justify-between text-[11.5px]">
                <span>LLM spend cap</span>
                <span className="font-mono-stat text-muted-foreground">
                  ${data.usage.llm_spend_usd.toFixed(2)} / ${data.usage.llm_cap_usd.toFixed(2)}
                  <span className="ml-1 opacity-70">({data.usage.llm_spend_pct.toFixed(1)}%)</span>
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-sm bg-secondary">
                <div
                  className={
                    'h-full transition-all ' +
                    (data.usage.llm_spend_pct >= 90 ? 'bg-rose-600'
                      : data.usage.llm_spend_pct >= 70 ? 'bg-amber-500' : 'bg-primary')
                  }
                  style={{ width: `${Math.min(100, data.usage.llm_spend_pct)}%` }}
                />
              </div>
              {data.usage.llm_spend_pct >= 90 && (
                <div className="mt-1 text-[10.5px] text-rose-700 dark:text-rose-400">
                  You&rsquo;re near the monthly LLM spend cap — top up with a token pack to keep drafting.
                </div>
              )}
            </div>
          )}
          {/* iter-p2-v — Usage-driven tier recommendation callout */}
          <TierRecommendationCallout onChoosePlan={onChoosePlan} />
        </div>
      )}

      {/* Card on file */}
      <div className="border-b border-border p-4">
        <div className="mb-1 text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">Card on file</div>
        {card ? (
          <div className="flex items-center gap-3 text-[12.5px]" data-testid="my-billing-card">
            <div className="flex h-8 w-12 items-center justify-center rounded-sm border border-border bg-secondary text-[9.5px] font-semibold uppercase tracking-wider">
              {brandLabel(card.brand).slice(0, 4)}
            </div>
            <div>
              <div>
                {brandLabel(card.brand)}{' '}
                <span className="font-mono-stat text-muted-foreground">•••• {card.last4 || '••••'}</span>
              </div>
              <div className="text-[10.5px] text-muted-foreground">
                Expires {String(card.exp_month || '').padStart(2, '0')}/{String(card.exp_year || '').slice(-2)}
                {card.funding && card.funding !== 'unknown' && (
                  <> · {card.funding}</>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-[11.5px] text-muted-foreground" data-testid="my-billing-no-card">
            No card on file with Stripe yet — click <b>Manage subscription</b> to add one.
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between gap-3 p-4">
        <div className="text-[11px] text-muted-foreground">
          Card details are managed on Stripe&apos;s hosted portal — we never see or store them.
        </div>
        <div className="flex items-center gap-2">
          {(status === 'active' || status === 'trialing' || status === 'past_due') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCancelOpen(true)}
              className="h-9 gap-1.5 text-[12px] text-muted-foreground hover:text-destructive"
              data-testid="my-billing-cancel-btn"
            >
              Cancel subscription
            </Button>
          )}
          <Button
            onClick={openPortal}
            disabled={openingPortal}
            size="sm"
            className="h-9 gap-1.5 text-[12px]"
            data-testid="my-billing-manage-btn"
          >
            {openingPortal ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
            {status === 'past_due'  ? 'Update card' :
             status === 'cancelled' ? 'Resubscribe' :
                                      'Manage subscription'}
          </Button>
        </div>
      </div>
      </div>
      {/* Feb-2026 · Grandfather-pricing rate-lock badge (self-hides). */}
      <GrandfatherBadge />
      <CancelSubscriptionDialog open={cancelOpen} onOpenChange={setCancelOpen} />
      {ulaCard}
      {/* iter55g50 — Business Dashboard entry (writing progress,
       * per-book P&L, marketing marketplace). Full-page route. */}
      <a href="/app/business"
         data-testid="my-billing-business-link"
         className="flex items-center justify-between gap-3 rounded-md border border-primary/40 bg-primary/5 p-3 transition hover:bg-primary/10">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" />
          <div>
            <div className="text-[12.5px] font-semibold">Author Business Dashboard</div>
            <div className="text-[11px] text-muted-foreground">
              Writing progress, sales per book, LLM cost, and marketing marketplace.
            </div>
          </div>
        </div>
        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
      </a>
      {/* iter55g30 — 3-month hiatus for Pro+/Studio subscribers. */}
      <HiatusCard />
      {/* iter55g31 — Publish-to-Stacks 70/30 royalties widget. */}
      <RoyaltiesCard />
      {/* iter-p3-v — 30-day AI-share trend sparkline. */}
      <AiShareTrendSparkline days={30} />
      {/* Feb-2026 · Revenue-lifts user-facing surfaces. Each component
       * self-hides when the corresponding admin flag is off. */}
      <BonusAddonsShelf />
      <ReferralCard />
      <MilestonesCard />
      {/* Feb-2026 · Zernio marketing ROI + best-bang recommendations. */}
      <ZernioAnalyticsCard />
    </div>
  );
}
